/**
 * Druckdatei serverseitig erzeugen (Vorstufe des Auto-Imports).
 *
 * Der Server (firebase_aq/functions-upscaler → renderPrintFileCall, printFile.ts) ist ein Port
 * des Exports in diesem Editor: Hintergrund → Artwork mit Position/Passepartout → Texte mit
 * Lesbarkeits-Wolke. Schalter „Server / Lokal" ganz unten im Editor: Standard ist Server, dann
 * erzeugen „Preview" und „Fulfill" die Datei dort statt im Browser.
 *
 * Fehler-Regel: Kann der Server die Datei nicht erzeugen (Fehler, Bild nur lokal vorhanden,
 * Zeichen ohne Schrift), muss der Task lokal importiert werden — der Schalter springt für diesen
 * Task automatisch auf „Lokal" (serverPrintFallback) und der Import läuft im Browser weiter.
 * Die gespeicherte Geräte-Einstellung bleibt dabei Server; der nächste Task startet wieder dort.
 *
 * Mit der Prop `serverImport` (Import-Karte der Live-Tasks) läuft im Server-Modus der GANZE Import
 * auf dem Server (Callable importLiveTaskCall = derselbe Weg wie der Auto-Import): Der Editor gibt
 * nur seinen Stand ab (Event „serverImport"), schließt und öffnet sofort den nächsten Task. Scheitert
 * der Server, steht das als `task.serverImport.state = "failed"` am Task — beim nächsten Öffnen
 * startet der Editor für diesen Task im Modus „Lokal".
 *
 * „Browser ↔ Server" (nur Admins) rendert beide und zeigt sie im Vergleichsdialog (Reiter
 * „Druckdatei"), ohne den Task zu verändern; die Server-Datei landet dabei in einem Test-Ordner.
 *
 * Emojis/Sonderzeichen mit Geräte-Schrift (Editor-Schalter „Emojis: System", derzeit Standard)
 * kann nur der Browser setzen → solche Tasks laufen immer lokal (emojiLocalReason).
 *
 * Erwartet vom Editor: emojiFont, localTextElements, task, orderName, customizationData, updatedCustomizationConfigData,
 * localBackground, artworkBaseUrl, upscaleMixActive, upscaleRegionMix, upscaleRefSize(),
 * renderPrintCanvas(), canvasToBlob(), resetCompareView(), compareTab, compareDialog.
 */
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../../../fb";
import { DEFAULT_EMOJI_FONT, hasSpecialGlyphs, unsupportedChars } from "@/emojiFont";

const SERVER_PRINT_KEY = "ohmyLiveEditorServerPrint";
const regionalFunctions = getFunctions(app, "europe-west1");

export default {
  data() {
    return {
      /** Geräte-Einstellung: „Preview"/„Fulfill" erzeugen die Druckdatei auf dem Server (Standard) statt im Browser */
      serverPrintFile: localStorage.getItem(SERVER_PRINT_KEY) !== "false",
      /** Grund, warum DIESER Task lokal importiert werden muss (Server-Rendering fehlgeschlagen) — sonst "" */
      serverPrintFallback: "",
      printCompare: { busy: false, error: "", browserUrl: null, serverUrl: null, meanDiff: null, changedPct: null, ms: null, emojiTexts: [] },
    };
  },
  computed: {
    /** Wirksamer Modus für diesen Task */
    printOnServer() {
      return this.serverPrintFile && !this.serverPrintFallback && !this.emojiLocalReason;
    },
    /** Enthält einer der Texte Emojis oder Sonderzeichen (♡ ★ ♪)? */
    textsHaveSpecialGlyphs() {
      return (this.localTextElements || []).some((el) => el && hasSpecialGlyphs(el.text));
    },
    /**
     * Emojis mit den Schriften dieses Rechners (Schalter „Emojis: System") gibt es nur im Browser —
     * solche Tasks werden lokal gerendert und importiert, auch wenn „Server" gewählt ist.
     */
    emojiLocalReason() {
      // Zeichen, die keine Server-Schrift kennt (z. B. kyrillisch): nur der Browser kann sie setzen
      const missing = [...new Set((this.localTextElements || []).flatMap((el) => (el ? unsupportedChars(el.text, el.font) : [])))];
      if (missing.length) return `Zeichen ohne Server-Schrift (${missing.join(" ")}): Dieser Task wird lokal importiert.`;
      return this.emojiFont === "system" && this.textsHaveSpecialGlyphs
        ? "Text enthält Emojis oder Sonderzeichen in der Schrift dieses Rechners: Dieser Task wird lokal importiert."
        : "";
    },
  },
  watch: {
    // Editor öffnet (auch für den nächsten Task): Geräte-Einstellung frisch lesen, Rückfall des letzten Tasks verwerfen
    value(open) {
      if (!open) return;
      this.emojiFont = DEFAULT_EMOJI_FONT;
      this.serverPrintFile = localStorage.getItem(SERVER_PRINT_KEY) !== "false";
      // Server-Import dieses Tasks ist schon einmal gescheitert → er muss lokal importiert werden
      const last = this.task && this.task.serverImport;
      this.serverPrintFallback = last && last.state === "failed" ? last.error || "Server-Import fehlgeschlagen" : "";
    },
  },
  beforeDestroy() {
    this.releasePrintCompare();
  },
  methods: {
    /** Schalter von Hand: gilt als Geräte-Einstellung; hebt einen automatischen Rückfall wieder auf */
    setPrintMode(server) {
      this.serverPrintFile = !!server;
      this.serverPrintFallback = "";
      localStorage.setItem(SERVER_PRINT_KEY, server ? "true" : "false");
    },

    /**
     * Druckdatei für „Preview"/„Fulfill": URL der Server-Datei — oder null, wenn lokal gerendert
     * werden muss (Modus „Lokal" oder Server-Rendering fehlgeschlagen → Schalter springt auf „Lokal").
     */
    async serverPrintUrlOrFallback() {
      if (!this.printOnServer) return null;
      try {
        const file = await this.renderPrintFileOnServer(false);
        if (file.emojiTexts && file.emojiTexts.length) throw new Error(`Zeichen ohne Server-Schrift: ${file.emojiTexts.join(" · ")}`);
        return file.url;
      } catch (err) {
        console.warn("Server-Rendering fehlgeschlagen — Task wird lokal importiert:", err);
        this.serverPrintFallback = (err && err.message) || String(err);
        return null;
      }
    },

    /** Was der Server zum Rendern braucht — exakt der Stand, den auch der Browser-Export nutzt */
    serverPrintPayload(test) {
      const cd = this.customizationData || {};
      const artworkUrl = this.artworkBaseUrl || cd.artworkImageUrl || cd.originalImage || null;
      if (!artworkUrl || !String(artworkUrl).startsWith("http")) {
        throw new Error("Dieses Bild liegt nur lokal im Browser vor (z. B. per Drag & Drop) — die Druckdatei kann dafür nur im Browser erzeugt werden.");
      }
      return {
        test: !!test,
        // nur mit „Emojis: Unsere" darf der Server Texte mit Emojis/Sonderzeichen setzen
        emojiFont: this.emojiFont,
        orderName: this.orderName || (this.task && this.task.orderName) || "order",
        config: JSON.parse(JSON.stringify(this.updatedCustomizationConfigData)),
        artworkUrl,
        // wie drawBackgroundLayer: ohne Hintergrund-Ebene weiß
        background: this.localBackground || null,
        layers: this.upscaleMixActive
          ? {
              finalUrl: cd.upscaledImage,
              rawUrl: cd.upscaleRawImage,
              refSize: this.upscaleRefSize(),
              regions: this.upscaleRegionMix.map((r) => ({ left: r.left, top: r.top, width: r.width, height: r.height, amount: r.amount, maskUrl: r.maskUrl || null })),
            }
          : null,
      };
    },

    /** Druckdatei auf dem Server erzeugen → { url, storagePath, width, height, ms, emojiTexts } */
    async renderPrintFileOnServer(test = false) {
      const call = httpsCallable(regionalFunctions, "renderPrintFileCall", { timeout: 300000 });
      const { data } = await call(this.serverPrintPayload(test));
      if (!data || !data.url) throw new Error("Der Server hat keine Druckdatei geliefert");
      return data;
    },

    releasePrintCompare() {
      const url = this.printCompare && this.printCompare.browserUrl;
      if (url && url.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          /* schon freigegeben */
        }
      }
    },

    /** Beide Wege rendern und nebeneinander zeigen — verändert den Task nicht */
    async runPrintCompare() {
      if (this.printCompare.busy) return;
      this.releasePrintCompare();
      this.printCompare = { busy: true, error: "", browserUrl: null, serverUrl: null, meanDiff: null, changedPct: null, ms: null, emojiTexts: [] };
      try {
        const [canvas, server] = await Promise.all([this.renderPrintCanvas(), this.renderPrintFileOnServer(true)]);
        const blob = await this.canvasToBlob(canvas, "image/jpeg", 0.9);
        const browserUrl = URL.createObjectURL(blob);
        const stats = await this.printDifference(canvas, server.url);
        this.printCompare = { busy: false, error: "", browserUrl, serverUrl: server.url, meanDiff: stats.meanDiff, changedPct: stats.changedPct, ms: server.ms, emojiTexts: server.emojiTexts || [] };
        this.resetCompareView();
        this.compareTab = "print";
        this.compareDialog = true;
      } catch (err) {
        console.error("Druckdatei-Vergleich:", err);
        this.printCompare = { ...this.printCompare, busy: false, error: (err && err.message) || String(err) };
      }
    },

    /** Mittlere Abweichung und Anteil deutlich abweichender Pixel (auf 1000 px Breite verkleinert) */
    async printDifference(browserCanvas, serverUrl) {
      const serverImg = await this.loadImage(serverUrl);
      const W = 1000;
      const H = Math.round((W * browserCanvas.height) / browserCanvas.width);
      const pixels = (source) => {
        const c = document.createElement("canvas");
        c.width = W;
        c.height = H;
        const ctx = c.getContext("2d");
        ctx.drawImage(source, 0, 0, W, H);
        return ctx.getImageData(0, 0, W, H).data;
      };
      const a = pixels(browserCanvas);
      const b = pixels(serverImg);
      let sum = 0;
      let changed = 0;
      for (let i = 0; i < a.length; i += 4) {
        const d = (Math.abs(a[i] - b[i]) + Math.abs(a[i + 1] - b[i + 1]) + Math.abs(a[i + 2] - b[i + 2])) / 3;
        sum += d;
        if (d > 16) changed++;
      }
      return { meanDiff: Number((sum / (W * H)).toFixed(2)), changedPct: Number(((changed / (W * H)) * 100).toFixed(2)) };
    },
  },
};
